import DateFormComponent from "components/shared/FormComponent/DateFormComponent";
import StyledButton from "components/shared/StyledButton";
import { formatTimestampDates } from "containers/TransferTermFormContainer/utils";
import type { Dispatch, SetStateAction } from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "store/store";
import type { ITransferTermSale } from "store/transferTermSalesStore";
import { ETransferTermSalesStatus, updateTransferTermSale } from "store/transferTermSalesStore";

import styles from "./style.module.scss";

interface IAdvanceUpdateModal {
    /**
     * Advance payment being updated.
     */
    currentPayment: ITransferTermSale | undefined;
    handleClose: () => void;
    /**
     * Used to update currentSales once advance modified.
     */
    setCurrentSales: Dispatch<SetStateAction<ITransferTermSale[]>>;
    currentSales: ITransferTermSale[];
}

/**
 * Modal accessed throught TransferTermDrawer to choose date of advance when set as Paid.
 */
const AdvanceUpdateModal = ({ currentPayment, handleClose, setCurrentSales, currentSales }: IAdvanceUpdateModal) => {
    const dispatch = useAppDispatch();
    const [t] = useTranslation();

    const [submitPending, setSubmitPending] = useState(false);

    const defaultValues = {
        paymentDate: null,
    };

    const {
        control,
        formState: { errors },
        handleSubmit,
    } = useForm({ defaultValues, mode: "all" });

    const onSubmit = async (data: { paymentDate: Date | null }) => {
        setSubmitPending(true);
        if (currentPayment) {
            const newAdvancePayment = {
                ...currentPayment,
                status: ETransferTermSalesStatus.PAYED,
                paymentDate: formatTimestampDates(data.paymentDate ?? new Date()),
            };
            const response = await dispatch(
                updateTransferTermSale({ saleId: currentPayment?.id, data: newAdvancePayment }),
            );
            if (response.meta.requestStatus === "fulfilled") {
                setSubmitPending(false);
                const newSales = currentSales.map((sale) => {
                    if (sale.id === newAdvancePayment.id && data.paymentDate) {
                        return { ...newAdvancePayment, paymentDate: data.paymentDate };
                    } else {
                        return sale;
                    }
                });
                await setCurrentSales(newSales);
                handleClose();
            } else {
                setSubmitPending(false);
            }
        }
    };

    return (
        <form className={styles["advance-update-modal"]} onSubmit={handleSubmit(onSubmit)}>
            <DateFormComponent
                control={control}
                controlName="paymentDate"
                label={t("TransferTerms.Drawer.ChoosePaymentDate")}
                required
                error={errors.paymentDate}
                maxYearDistance={10}
            />
            <div className={styles["buttons"]}>
                <StyledButton buttonType="outlined" onClick={handleClose}>
                    {t("TransferTerms.PaymentModal.Cancel")}
                </StyledButton>
                <StyledButton buttonType="primary" type="submit" loading={submitPending}>
                    {t("TransferTerms.PaymentModal.Confirm")}
                </StyledButton>
            </div>
        </form>
    );
};

export default AdvanceUpdateModal;
